/** Bugs and requests. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface FeedbackItem {
  id: string
  title: string
  description: string
  kind: 'bug' | 'request' | 'question'
  status: 'new' | 'planned' | 'in_progress' | 'resolved' | 'declined'
  severity: 'low' | 'normal' | 'high' | 'blocking'
  page: string
  reportedBy: string
  workItemId: string | null
  resolution: string
  resolvedAt: string | null
  createdDate: string
  isOpen: boolean
  attachments: FeedbackAttachment[]
  rowVersion: number
}

export interface FeedbackAttachment {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  uploadedBy: string
}

/** Mirrors the API's limits, so a file is refused before it is uploaded rather than after. */
export const ATTACHMENT_LIMITS = {
  maxBytes: 5 * 1024 * 1024,
  maxPerItem: 5,
  types: ['image/png', 'image/jpeg'],
} as const

const key = ['feedback'] as const

export function useFeedback() {
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/feedback', {})
      return data as unknown as { items: FeedbackItem[]; openCount: number }
    },
  })
}

export function useSubmitFeedback() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      title: string
      description?: string
      kind?: FeedbackItem['kind']
      severity?: FeedbackItem['severity']
      page?: string
    }) => {
      const { data } = await api.POST('/api/v1/feedback', { body: body as never })
      return data as unknown as FeedbackItem
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

export function useTriageFeedback() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      status?: FeedbackItem['status']
      severity?: FeedbackItem['severity']
      resolution?: string
      row_version?: number
    }) => {
      const { data } = await api.PATCH('/api/v1/feedback/{item_id}', {
        params: { path: { item_id: id } },
        body: body as never,
      })
      return data as unknown as FeedbackItem
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

/** The reporter rewording their own report. The API refuses anyone else. */
export function useEditFeedbackReport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      title: string
      description: string
      row_version: number
    }) => {
      const { data } = await api.PATCH('/api/v1/feedback/{item_id}/report', {
        params: { path: { item_id: id } },
        body: body as never,
      })
      return data as unknown as FeedbackItem
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

/** Attach one image. Sent as a form upload; the API decides the type from the bytes. */
export function useUploadFeedbackAttachment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      const form = new FormData()
      form.append('file', file, file.name || 'screenshot.png')
      const { data } = await api.POST('/api/v1/feedback/{item_id}/attachments', {
        params: { path: { item_id: itemId } },
        body: form as never,
      })
      return data as unknown as FeedbackAttachment
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

export function useRemoveFeedbackAttachment() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ itemId, attachmentId }: { itemId: string; attachmentId: string }) => {
      await api.DELETE('/api/v1/feedback/{item_id}/attachments/{attachment_id}', {
        params: { path: { item_id: itemId, attachment_id: attachmentId } },
      })
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

/**
 * The image itself, as a Blob.
 *
 * Fetched through the API with the user's token rather than as a plain <img src>, which
 * cannot carry one — the images are visible to people who can read the queue and to
 * nobody who merely has the link. An attachment's bytes never change, so it is cached
 * for the session.
 */
export function useAttachmentBlob(itemId: string, attachmentId: string) {
  return useQuery({
    queryKey: ['feedback-attachment', attachmentId],
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/feedback/{item_id}/attachments/{attachment_id}', {
        params: { path: { item_id: itemId, attachment_id: attachmentId } },
        parseAs: 'blob',
      })
      return data as unknown as Blob
    },
  })
}
